import type { localeBody } from '@porotutu/api/features/adm/profile/model'
import type { adminProfileResource } from '@porotutu/api/features/adm/profile/resource'
import { PROFILE, unwrap } from '@porotutu/react/api'
import type { ApiError } from '@porotutu/react/errors'
import { mutationOptions, type QueryClient, queryOptions } from '@tanstack/react-query'
import { adm } from '../api/client'

export type Profile = ReturnType<typeof adminProfileResource>

export async function readProfile(): Promise<Profile | null> {
  const { data, error } = await adm.profile.get()

  if (error === null) return data

  if (error.status === 401) return null

  throw error
}

export const profileQuery = queryOptions({
  queryKey: PROFILE,
  queryFn: readProfile,
  staleTime: Number.POSITIVE_INFINITY,
  gcTime: Number.POSITIVE_INFINITY,
  retry: false,
})

export type LocaleBody = typeof localeBody.static

export const changeLocaleMutation = (queryClient: QueryClient) =>
  mutationOptions<Profile, ApiError, LocaleBody, { previous: Profile | null | undefined }>({
    mutationKey: [...PROFILE, 'locale'],
    mutationFn: async (body) => unwrap(await adm.profile.locale.patch(body)),
    onMutate: ({ locale }) => {
      const previous = queryClient.getQueryData(profileQuery.queryKey)

      if (previous) {
        const hopeful: Profile = { ...previous, locale }

        queryClient.setQueryData(profileQuery.queryKey, hopeful)
      }

      return { previous }
    },
    onError: (_error, _body, context) => {
      if (context) queryClient.setQueryData(profileQuery.queryKey, context.previous)
    },
    onSuccess: (profile) => {
      queryClient.setQueryData(profileQuery.queryKey, profile)
    },
  })
